/**
 * ListenerRegistry
 *
 * Global singleton for managing Firestore listeners across the application.
 * Provides atomic cleanup on project change and prevents memory leaks.
 *
 * @module firebase-core/services
 */

/**
 * Types of listeners that can be registered
 */
export type ListenerType =
  | 'project'
  | 'audience'
  | 'actor'
  | 'task'
  | 'strategy'
  | 'block'
  | 'screen'
  | 'onboarding'
  | 'version'
  | 'other';

/**
 * Options for registering a listener
 */
export interface ListenerOptions {
  /** Project ID this listener belongs to */
  projectId: string;

  /** Type of data being listened to */
  type: ListenerType;

  /** Parent listener ID (for nested listeners like actors -> audience) */
  parentId?: string;

  /** Optional description for debugging */
  description?: string;
}

/**
 * Internal entry for a registered listener
 */
interface ListenerEntry {
  /** Unsubscribe function from Firestore */
  unsubscribe: () => void;

  /** Registration options */
  options: ListenerOptions;

  /** Registration timestamp */
  registeredAt: Date;
}

/**
 * ListenerRegistry - Global singleton for managing Firestore listeners
 *
 * Features:
 * - Atomic cleanup on project change
 * - Parent-child relationship tracking
 * - Type-based querying
 * - Duplicate prevention
 *
 * @example
 * ```typescript
 * const registry = ListenerRegistry.getInstance();
 *
 * // Register a listener
 * const unsubscribe = onSnapshot(docRef, callback);
 * registry.register('my-listener', unsubscribe, {
 *   projectId: 'project-123',
 *   type: 'audience',
 * });
 *
 * // Cleanup on project change
 * registry.cleanupProject('project-123');
 * ```
 */
export class ListenerRegistry {
  private static instance: ListenerRegistry | null = null;

  /** Map of listener ID to listener entry */
  private listeners: Map<string, ListenerEntry> = new Map();

  /** Map of project ID to set of listener IDs */
  private projectListeners: Map<string, Set<string>> = new Map();

  /** Map of parent ID to set of child listener IDs */
  private childListeners: Map<string, Set<string>> = new Map();

  /** Private constructor for singleton */
  private constructor() {}

  /**
   * Get the singleton instance
   */
  static getInstance(): ListenerRegistry {
    if (!ListenerRegistry.instance) {
      ListenerRegistry.instance = new ListenerRegistry();
    }
    return ListenerRegistry.instance;
  }

  /**
   * Reset the singleton instance (for testing)
   */
  static resetInstance(): void {
    if (ListenerRegistry.instance) {
      ListenerRegistry.instance.cleanupAll();
    }
    ListenerRegistry.instance = null;
  }

  /**
   * Register a new listener
   *
   * @param id - Unique identifier for the listener
   * @param unsubscribe - Unsubscribe function from Firestore
   * @param options - Listener options (projectId, type, parentId)
   */
  register(id: string, unsubscribe: () => void, options: ListenerOptions): void {
    // If listener already exists, unregister it first
    if (this.listeners.has(id)) {
      this.unregister(id);
    }

    // Create entry
    const entry: ListenerEntry = {
      unsubscribe,
      options,
      registeredAt: new Date(),
    };

    // Store in main map
    this.listeners.set(id, entry);

    // Track by project
    if (!this.projectListeners.has(options.projectId)) {
      this.projectListeners.set(options.projectId, new Set());
    }
    this.projectListeners.get(options.projectId)!.add(id);

    // Track by parent if specified
    if (options.parentId) {
      if (!this.childListeners.has(options.parentId)) {
        this.childListeners.set(options.parentId, new Set());
      }
      this.childListeners.get(options.parentId)!.add(id);
    }
  }

  /**
   * Unregister a listener and call its unsubscribe function
   *
   * @param id - Listener ID to unregister
   */
  unregister(id: string): void {
    const entry = this.listeners.get(id);
    if (!entry) {
      return;
    }

    // Call unsubscribe with error handling
    try {
      entry.unsubscribe();
    } catch (error) {
      console.error(`[ListenerRegistry] Error unsubscribing listener ${id}:`, error);
    }

    // Remove from project tracking
    const projectSet = this.projectListeners.get(entry.options.projectId);
    if (projectSet) {
      projectSet.delete(id);
      if (projectSet.size === 0) {
        this.projectListeners.delete(entry.options.projectId);
      }
    }

    // Remove from parent tracking
    if (entry.options.parentId) {
      const childSet = this.childListeners.get(entry.options.parentId);
      if (childSet) {
        childSet.delete(id);
        if (childSet.size === 0) {
          this.childListeners.delete(entry.options.parentId);
        }
      }
    }

    // Remove from main map
    this.listeners.delete(id);
  }

  /**
   * Cleanup all listeners for a specific project
   *
   * @param projectId - Project ID to cleanup
   */
  cleanupProject(projectId: string): void {
    const listenerIds = this.projectListeners.get(projectId);
    if (!listenerIds) {
      return;
    }

    // Copy set to avoid modification during iteration
    const idsToRemove = Array.from(listenerIds);
    for (const id of idsToRemove) {
      this.unregister(id);
    }
  }

  /**
   * Cleanup all child listeners of a parent
   *
   * @param parentId - Parent listener ID
   */
  cleanupChildren(parentId: string): void {
    const childIds = this.childListeners.get(parentId);
    if (!childIds) {
      return;
    }

    // Copy set to avoid modification during iteration
    const idsToRemove = Array.from(childIds);
    for (const id of idsToRemove) {
      this.unregister(id);
    }
  }

  /**
   * Cleanup all registered listeners
   */
  cleanupAll(): void {
    // Copy keys to avoid modification during iteration
    const allIds = Array.from(this.listeners.keys());
    for (const id of allIds) {
      this.unregister(id);
    }
  }

  /**
   * Check if a listener exists
   *
   * @param id - Listener ID to check
   */
  has(id: string): boolean {
    return this.listeners.has(id);
  }

  /**
   * Get all listener IDs for a project
   *
   * @param projectId - Project ID
   */
  getListenersByProject(projectId: string): string[] {
    const listenerIds = this.projectListeners.get(projectId);
    return listenerIds ? Array.from(listenerIds) : [];
  }

  /**
   * Get all listener IDs of a specific type
   *
   * @param type - Listener type
   */
  getListenersByType(type: ListenerType): string[] {
    const result: string[] = [];
    for (const [id, entry] of this.listeners) {
      if (entry.options.type === type) {
        result.push(id);
      }
    }
    return result;
  }

  /**
   * Get the count of active listeners
   */
  getActiveCount(): number {
    return this.listeners.size;
  }

  /**
   * Get debug info about registered listeners
   */
  getDebugInfo(): {
    totalListeners: number;
    byProject: Record<string, number>;
    byType: Record<string, number>;
  } {
    const byProject: Record<string, number> = {};
    const byType: Record<string, number> = {};

    for (const [projectId, listenerSet] of this.projectListeners) {
      byProject[projectId] = listenerSet.size;
    }

    for (const [, entry] of this.listeners) {
      const type = entry.options.type;
      byType[type] = (byType[type] || 0) + 1;
    }

    return {
      totalListeners: this.listeners.size,
      byProject,
      byType,
    };
  }
}
